import { useMemo, useState } from "react"
import type { FC } from "react"
import { useTranslation } from "react-i18next"
import {
  alpha,
  Box,
  Checkbox,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import type { Theme } from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"

import Pagination from "widgets/common/Pagination"
import type { CsvSourceTelemetry } from "types/csv.types"

interface CsvTelemetryTableProps {
  telemetries: CsvSourceTelemetry[]
  selected: Set<string>
  onToggle: (apiName: string) => void
}

const PAGE_SIZE = 6
const CHECKBOX_COL_WIDTH = 42
const MIN_TABLE_WIDTH = 380
const ROW_MIN_HEIGHT = 48
const NAME_COL_MAX_WIDTH = { xs: 150, sm: 280 }

const CsvTelemetryTable: FC<CsvTelemetryTableProps> = ({
  telemetries,
  selected,
  onToggle,
}) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(0)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return telemetries
    return telemetries.filter(
      (tel) =>
        tel.label.toLowerCase().includes(q) || tel.apiName.toLowerCase().includes(q),
    )
  }, [telemetries, search])

  const pageItems = useMemo(
    () => filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [filtered, page],
  )

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(0)
  }

  const hasPagination = filtered.length > PAGE_SIZE

  // Rows are separated by a subtler line than the header's. The last one only
  // draws a full divider when it has to be separated from the pagination.
  const getRowBorder = (isLastRow: boolean) => (theme: Theme) => {
    if (!isLastRow) return `1px solid ${alpha(theme.palette.neutral[400], 0.4)}`
    return hasPagination ? `1px solid ${theme.palette.neutral[400]}` : "none"
  }

  return (
    <Stack sx={{ width: "100%" }}>
      <Box
        sx={{
          p: 1,
          borderBottom: (theme) => `1px solid ${theme.palette.neutral[400]}`,
        }}
      >
        <TextField
          fullWidth
          size="small"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder={t("csv_drawer.SEARCH")}
          sx={{ mb: 0 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ width: "max-content", minWidth: `max(${MIN_TABLE_WIDTH}px, 100%)` }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              px: 1,
              py: 1,
              minHeight: ROW_MIN_HEIGHT,
              borderBottom: (theme) => `1px solid ${theme.palette.neutral[400]}`,
            }}
          >
            <Box sx={{ width: CHECKBOX_COL_WIDTH, flexShrink: 0 }} />
            <Typography
              variant="caption"
              sx={{ flex: 1.4, maxWidth: NAME_COL_MAX_WIDTH, fontWeight: 600 }}
            >
              {t("csv_drawer.NAME")}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                flexGrow: 1.4,
                flexShrink: 0,
                flexBasis: 0,
                fontWeight: 600,
                pl: 2,
                whiteSpace: "nowrap",
              }}
            >
              {t("csv_drawer.FIELD")}
            </Typography>
          </Stack>

          {pageItems.map((tel, index) => (
            <Stack
              key={tel.apiName}
              direction="row"
              alignItems="center"
              sx={{
                px: 1,
                py: 1,
                minHeight: ROW_MIN_HEIGHT,
                borderBottom: getRowBorder(index === pageItems.length - 1),
                cursor: "pointer",
                "&:hover": { backgroundColor: (theme) => theme.palette.action.hover },
              }}
              onClick={() => onToggle(tel.apiName)}
            >
              <Box
                sx={{
                  width: CHECKBOX_COL_WIDTH,
                  flexShrink: 0,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Checkbox
                  size="small"
                  sx={{ p: 0.75 }}
                  checked={selected.has(tel.apiName)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => onToggle(tel.apiName)}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{ flex: 1.4, maxWidth: NAME_COL_MAX_WIDTH }}
              >
                {tel.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  flexGrow: 1.4,
                  flexShrink: 0,
                  flexBasis: 0,
                  pl: 2,
                  color: (theme) =>
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[400]
                      : theme.palette.grey[800],
                  whiteSpace: "nowrap",
                }}
              >
                {tel.apiName}
              </Typography>
            </Stack>
          ))}

          {filtered.length === 0 && (
            <Typography
              variant="body2"
              sx={{ p: 2, color: (theme) => theme.palette.text.secondary }}
            >
              {t("csv_drawer.NO_TELEMETRIES")}
            </Typography>
          )}
        </Box>
      </Box>

      {hasPagination && (
        <Box sx={{ px: 1, py: 1 }}>
          <Pagination
            count={filtered.length}
            page={page}
            setPage={setPage}
            limit={PAGE_SIZE}
          />
        </Box>
      )}
    </Stack>
  )
}

export default CsvTelemetryTable
